import { AssertTrue, ValidateEnumIsSubtype } from "@/domains/typing/types";
import { ApiSchema } from "@/modules/api";
import { AudioRecordingIndexData } from "@/store/audio-recordings/audio-recording-indexes";
import { SyncUpdateValue } from "@/store/sync/types";

export type AudioRecordingModelData = ApiSchema["AudioRecordingSyncModelDataResponseSchema"];

export type AudioRecordingSyncUpdateValue = ApiSchema["AudioRecordingSyncModelResponseSchema"];

export type IndexedAudioRecordingSyncUpdateValue = SyncUpdateValue<AudioRecordingModelData> &
  AudioRecordingIndexData;

// TODO - We should update the backend with an enum for these individual statuses.
export type AudioRecordingRecordingStatusValues = ApiSchema["AudioRecordingUploadStatus"];
export type AudioRecordingUploadStatusValues = ApiSchema["AudioRecordingUploadStatus"];
export type AudioRecordingProcessingStatusValues = ApiSchema["AudioRecordingUploadStatus"];

export enum AudioRecordingRecordingStatus {
  INITIAL = "INITIAL",
  IN_PROGRESS = "IN_PROGRESS",
  COMPLETED = "COMPLETED",
  ERROR = "ERROR",
}

export enum AudioRecordingUploadStatus {
  INITIAL = "INITIAL",
  IN_PROGRESS = "IN_PROGRESS",
  COMPLETED = "COMPLETED",
  ERROR = "ERROR",
}

export enum AudioRecordingProcessingStatus {
  INITIAL = "INITIAL",
  IN_PROGRESS = "IN_PROGRESS",
  COMPLETED = "COMPLETED",
  ERROR = "ERROR",
}

type _ValidateAudioRecordingRecordingStatusValues = AssertTrue<
  ValidateEnumIsSubtype<AudioRecordingRecordingStatus, AudioRecordingRecordingStatusValues>
>;

type _ValidateAudioRecordingUploadStatusValues = AssertTrue<
  ValidateEnumIsSubtype<AudioRecordingUploadStatus, AudioRecordingUploadStatusValues>
>;

type _ValidateAudioRecordingProcessingStatusValues = AssertTrue<
  ValidateEnumIsSubtype<AudioRecordingProcessingStatus, AudioRecordingProcessingStatusValues>
>;

/**
 * Event kinds that can be appended to an AudioRecording's value_json.app_events array.
 * These events track the lifecycle of an audio recording from the client's perspective.
 *
 * Must match backend: app/src/models/audio_recordings/app_events.py
 */
export enum AudioRecordingAppEventKind {
  // Local recording lifecycle
  STARTED_LOCAL = "started_local",
  PAUSED_LOCAL = "paused_local",
  RESUMED_LOCAL = "resumed_local",
  COMPLETED_LOCAL = "completed_local",

  // Upload lifecycle
  STARTED_UPLOADING = "started_uploading",
  COMPLETED_UPLOADING = "completed_uploading",
  ERROR_UPLOADING = "error_uploading",

  // Transcription connection
  TRANSCRIPTION_DISCONNECTED = "transcription_disconnected",
  TRANSCRIPTION_RECONNECTED = "transcription_reconnected",
  TRANSCRIPTION_COMPROMISED = "transcription_compromised",

  // Voice connection
  VOICE_DISCONNECTED = "voice_disconnected",
  VOICE_RECONNECTED = "voice_reconnected",

  // Errors
  ERROR_STARTING_LOCAL = "error_starting_local",
  ERROR_RESUMING_LOCAL = "error_resuming_local",
}

/**
 * An event that occurred during the lifecycle of an audio recording.
 * Stored in AudioRecording.value_json.app_events array.
 */
export interface AudioRecordingAppEvent {
  kind: AudioRecordingAppEventKind;
  occurred_at: string;
  properties?: Record<string, unknown>;
}

/**
 * Reasons why recording duration calculation may fail.
 */
export enum RecordingDurationFailureReason {
  NO_APP_EVENTS = "no_app_events",
  MISSING_STARTED_EVENT = "missing_started_event",
  MISSING_COMPLETED_EVENT = "missing_completed_event",
  INVALID_TIMESTAMPS = "invalid_timestamps",
}
